/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

/**
 * The patient-facing report as data: what the patient view of a Heart
 * Report shows, flattened for a renderer that has no access to the report
 * rules (the server-side PDF). Every string comes from the same core helper
 * the patient view components render with, so the two cannot drift:
 *
 * - card set and order: `reportFeatures(…, { viewMode: "patient" })`
 *   (HeartReportCards)
 * - title: `reportCardTitle`; result wording beside it:
 *   `reportResultText(…, { patientFacing: true })` (HeartReportCards)
 * - status colour: `effectiveFeatureColorClass` (the card's data-status);
 *   status label: `getStatusLabel` for that colour
 * - observed value: only for the cards the patient view shows a numeric on
 *   (bpm-type cards, or cards opted in with `showNumericInPatient`), and
 *   only when there is a displayable value (HeartReportCards)
 * - scale: the card's traffic-light scale via the shared scale module —
 *   marker position (and, on a timing card that carries a range — Heart
 *   Rate, SYS:DIA Ratio or SYS:DIA Duration — the two ends the card joins
 *   with a band), gradient stops of the card's track variant, zone labels —
 *   plus the reference-range line the numeric cards show (HeartReportCards,
 *   scale.ts)
 * - info: the card's info (ⓘ) help text — `getFeatureDescription`, the
 *   tooltip's aria-label (HeartReportCards)
 * - explanation: the card's "What this means" body — the patient note
 *   (`patientNoteDisplay`) or, when there is none, the generated
 *   interpretation (`generateInterpretation`) (HeartReportCards)
 * - recording notes: each reviewer observation's Report note, in order —
 *   labeled with its recording position; nothing else of the observations
 *   (the viewer's selections and internal note are HCP review context), and
 *   no qualification line (the Report notes section has none)
 *   (HeartReportRecordingNotes)
 * - follow-up note: `patientFollowUpText` over the report's follow-up note,
 *   the paragraph the follow-up block shows (HeartReportFollowUp)
 * - limitation / emergency text: the constants the follow-up block renders
 *   (HeartReportFollowUp)
 * - summary: the patient view renders no summary block, so none
 */

import {
  EMERGENCY_BOILERPLATE_TEXT,
  REPORT_LIMITATION_TEXT,
  currentHeartRateBpm,
  displayObservedValue,
  displayReferenceRange,
  effectiveFeatureColorClass,
  generateInterpretation,
  getFeatureDescription,
  getStatusLabel,
  hasDisplayableObservedValue,
  patientFollowUpText,
  patientNoteDisplay,
  recordingNoteReportNote,
  reportCardTitle,
  reportFeatures,
  reportResultText,
} from "./core"
import { scaleGradientStops, scaleLabels, scaleMarkerPct, scaleRangePct } from "./scale"
import {
  PcgPatientReportCard,
  PcgPatientReportDocument,
  PcgPatientReportNote,
  PcgPatientReportScale,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

const STATUS_COLORS: PcgPatientReportCard["statusColor"][] = ["green", "amber", "red", "grey"]

const statusColorOf = (colorClass: string): PcgPatientReportCard["statusColor"] =>
  (STATUS_COLORS as string[]).includes(colorClass) ? (colorClass as PcgPatientReportCard["statusColor"]) : "grey"

/** Same rule as the patient card: numerics only on bpm cards or cards opted in. */
const showsNumericInPatientView = (feature: any): boolean =>
  feature.type === "bpm" || feature.showNumericInPatient === true

/**
 * The card's traffic-light scale as data, from the same helpers the card
 * renders with: the marker position, the gradient stops of the track variant
 * the card uses, the zone labels, and the reference-range line the card
 * shows under the scale (only numeric cards show one — same rule as the
 * observed value).
 */
const cardScale = (feature: any, hr: number | null): PcgPatientReportScale => {
  const referenceText = showsNumericInPatientView(feature) ? displayReferenceRange(feature, hr) : ""
  const range = scaleRangePct(feature, hr)
  // The document carries the two positions; the marker titles are on-screen only
  const rangePct = range ? { minPct: range.minPct, maxPct: range.maxPct } : null
  return {
    // A range card's single marker, for a renderer without ranges, is the midpoint
    markerPct: rangePct ? (rangePct.minPct + rangePct.maxPct) / 2 : scaleMarkerPct(feature, hr),
    rangePct,
    gradient: scaleGradientStops(feature).map((stop) => ({ ...stop })),
    labels: scaleLabels(feature).map((label) => ({ ...label })),
    referenceText: referenceText || null,
  }
}

/**
 * The Report notes as the report's "Report notes" section shows them: one
 * {label, text} entry per note that has a Report note, labeled the way the
 * on-screen section labels its inner panels. The viewer's observations and
 * internal note are HCP review context and never reach a report document.
 */
const reviewerObservationNotes = (notes: unknown): PcgPatientReportNote[] =>
  (Array.isArray(notes) ? notes : [])
    .filter((note: any) => note && recordingNoteReportNote(note))
    .map((note: any, index: number): PcgPatientReportNote => {
      const position = String(note.position || `Recording ${note.recordingSequence || index + 1}`)
      return {
        label: `Recording ${String(note.recordingSequence || index + 1)} · ${position}`,
        text: recordingNoteReportNote(note),
      }
    })

const buildPatientReportDocument = (
  report: unknown,
  { patientName, examDate }: { patientName: string; examDate: string }
): PcgPatientReportDocument => {
  const document = report as { features?: any[]; recordingNotes?: unknown; followUpNote?: { patientText?: string } }
  const features = document.features ?? []
  const hr = currentHeartRateBpm(features)
  const cards = (reportFeatures(features, { viewMode: "patient" }) as any[]).map((feature): PcgPatientReportCard => {
    const colorClass = effectiveFeatureColorClass(feature, hr)
    const note = patientNoteDisplay(feature, hr)
    return {
      title: reportCardTitle(feature),
      statusLabel: getStatusLabel(feature, colorClass),
      statusColor: statusColorOf(colorClass),
      observed:
        showsNumericInPatientView(feature) && hasDisplayableObservedValue(feature)
          ? displayObservedValue(feature)
          : null,
      explanation: note || generateInterpretation(feature, colorClass),
      info: getFeatureDescription(feature.name) || null,
      result: reportResultText(feature, colorClass, { patientFacing: true }),
      scale: cardScale(feature, hr),
    }
  })
  const recordingNotes = reviewerObservationNotes(document.recordingNotes)
  return {
    patientName,
    examDate,
    summaryText: null,
    cards,
    recordingNotes,
    recordingNoteInstruction: null,
    followUpText: patientFollowUpText(document.followUpNote?.patientText || "", features, hr) || null,
    limitationText: REPORT_LIMITATION_TEXT,
    emergencyText: EMERGENCY_BOILERPLATE_TEXT,
  }
}

export { buildPatientReportDocument, reviewerObservationNotes }
